/* eslint-disable no-undef */
import { browser, os } from '@mirinae/js-utils/utils/userAgent';
import Localization from '@mirinae/shared/il8n';

// GA3 event recording - NO-OP -  deprecated - GA3 no  longer live, need to remove or recodew all calls to Evet() throughout the codebase
export const Event = (category, action, label) => {
};

let isFlutterInAppWebViewReady = false;
window.addEventListener('flutterInAppWebViewPlatformReady', () => {
    isFlutterInAppWebViewReady = true;
    deliverUserId(); // replay a user ID that was set before the bridge was ready
});
//  send GTM Event to webview flutter. using javascript channel
// migrate to new flutter library
const sendEvent = (command, name, parameters) => {
    if (!command || !name) {
        return;
    }

    const message = {
        command,
        name,
        parameters: { ...parameters },
    };

    if (window.GtmChannel) {
        window.GtmChannel.postMessage(JSON.stringify(message));
    } else if (isFlutterInAppWebViewReady || window.inAppView) {
        window.flutter_inappwebview.callHandler('GtmChannel', message);
    }
};

// --- automatic route-change page_view -----------------------------------------------------
// Many route components send their own GTM.pageView() with a richer title and params (lesson
// name, looked-up word, grammar pattern), and some routes deliberately send several per route
// (explorer.input then explorer.analysis on the same path). So the automatic pageview below is
// a FALLBACK only: it is scheduled on each route change and cancelled if the route sends any
// pageview of its own. The delay gives lazy()-loaded route components time to mount and send
// theirs first -- without it, a slow chunk would produce a duplicate.
const AUTO_PAGEVIEW_DELAY = 800; // ms
let lastPageViewPath = null;     // path of the most recent page_view, manual or automatic
let pendingAutoPageView = null;

// --- GA4 user ID ---------------------------------------------------------------------------
// Without a user ID, GA4 counts browsers, not people. We send the account's database id -- a
// pseudonymous identifier: not a name or email, but joinable to the account by design -- and only
// for registered accounts. A guest's id is also its login token (the server logs a guest in by
// id), so it must never leave the site. Local signups awaiting email verification are still role
// 'guest' and are skipped too.
//
// Transport follows Google's guidance for an ID that becomes known after the tag has loaded
// (developers.google.com/analytics/devguides/collection/ga4/user-id): gtag('set', { user_id }),
// an explicit null on logout, and never an event-level parameter. gtag() is the standard shim; it
// queues the command on the dataLayer the GTM snippet created, for the Google tag GTM loaded.
//
// Inside the Flutter webview, events go to Firebase instead, so the ID is forwarded over the
// flutter_inappwebview bridge as a `setUserId` command, and replayed when the bridge becomes
// ready. Builds on the older JavaScriptChannel (window.GtmChannel) are sent nothing: none of them
// can handle the command, and we cannot see what their handler does with an unknown one.
//
// Callers: authAPI reports every user change synchronously, BEFORE it updates the user context, so
// the ID is in place before React renders anything that sends analytics, layout effects included
// (registered in appBase.js). appBase.js also calls setUser as a backstop. Both are deduped here.
function gtag() { window.dataLayer = window.dataLayer || []; window.dataLayer.push(arguments); } // eslint-disable-line prefer-rest-params

// Off pending John's decision on whether sending account ids to GA4 is allowed (on hold since 2026-10-27). setUser
// queues or sends null instead of an account id; whether an id set earlier is cleared depends on the receiving tag or
// native handler.
const SEND_ACCOUNT_ID = false;

const eligibleUserId = user => (
    SEND_ACCOUNT_ID && user && user._id && user.role && user.role !== 'guest' && user.loginType !== 'guest'
        ? String(user._id) : null
);

let desiredUserId;   // the ID analytics should use; null = nobody identified; undefined = not yet known
let deliveredUserId; // the ID last handed over successfully

function deliverUserId() {
    if (desiredUserId === undefined || desiredUserId === deliveredUserId) return;
    const id = desiredUserId;
    try {
        if (window.GtmChannel || window.flutter_inappwebview) {
            if (!window.GtmChannel && (isFlutterInAppWebViewReady || window.inAppView)) {
                const pending = window.flutter_inappwebview.callHandler('GtmChannel', { command: 'setUserId', name: 'user_id', parameters: { user_id: id } });
                // marked delivered only once the call is made without throwing; a later rejection
                // resets it, so either failure leaves the next setUser free to retry
                deliveredUserId = id;
                Promise.resolve(pending).catch(() => { if (deliveredUserId === id) deliveredUserId = undefined; });
            }
            // bridge not ready yet: left undelivered, replayed on flutterInAppWebViewPlatformReady
            return;
        }
        gtag('set', { user_id: id }); // null clears it on logout
        deliveredUserId = id;
        console.log('GA4 user id', id ? 'set' : 'cleared');
    } catch (e) {
        // analytics must never break the app; left undelivered, so the next setUser retries
    }
}

// GA4 + GTM instrumnentation API
//
// The GTM container itself is loaded by the standard snippet in public/templates/*.html, not
// from here -- it needs to load during HTML parse rather than after the bundle. There is
// deliberately no initialize() any more: loading the same container twice risks duplicate tag
// fires. The pushes below just append to the dataLayer the snippet has already created.
const GTM = {
    // called with the new user (or null on logout) by authAPI.onUserChange, and by appBase.js
    setUser: (user) => {
        desiredUserId = eligibleUserId(user);
        deliverUserId();
    },

    // the ID setUser would send for this user, or null; appBase.js keys its backstop effect on it
    eligibleUserId,

    pageView: (title, props = {} , app) => {
        lastPageViewPath = window.location.pathname;
        if (window.GtmChannel || window.flutter_inappwebview) {
            sendEvent('logEvent', 'page_view', { title, property: 'app', app, language: Localization.getLanguage(), ...props });
            return;
        }

        window.dataLayer.push({
            event: undefined,
            action: undefined,
            page: undefined,
        });
        window.dataLayer.push({
            event: 'page_view',
            page: {
                title,
                app,
                property: browser.isMirinae ? 'app' : 'web',
                os: os.name,
                // the user's chosen Mirinae UI language, NOT the browser locale that GA4 collects
                // automatically -- the two diverge often for our multilingual audience
                language: Localization.getLanguage(),
                ...props,
            },
        });
        console.log('GA4 pageView', title, app, props);
    },

    // called by the shared router listener in appBase.js on every route change, including the
    // first render. Sends a page_view only if the route did not send one of its own.
    autoPageView: (app) => {
        if (pendingAutoPageView) clearTimeout(pendingAutoPageView);
        const path = window.location.pathname;
        // React runs child effects BEFORE parent ones, so a route component's own pageview has
        // usually already fired by the time this runs -- hence checking the path rather than a
        // since-navigation flag, which would wipe that record.
        if (lastPageViewPath === path) return;
        pendingAutoPageView = setTimeout(() => {
            pendingAutoPageView = null;
            if (lastPageViewPath === path) return;          // a lazy route mounted and sent its own
            if (window.location.pathname !== path) return;  // navigated again before we fired
            GTM.pageView(path, { auto: true }, app);
        }, AUTO_PAGEVIEW_DELAY);
    },

    userAction: (code, props = {}, app) => {
        const actionCode = code.replace(/-/g, '_'); // firebase prohibits '-' in event names, make all reporting consistently snake-case
        if (window.GtmChannel || window.flutter_inappwebview) {
            sendEvent('logEvent', `explorer_${actionCode}`, { action: actionCode, app, property: 'app', ...props });
            return;
        }
        window.dataLayer.push({ event: undefined, action: undefined });
        window.dataLayer.push({
            event: 'explorer_user_action', // GTM maps this to GA4 event explorer_<action.code>
            action: {
                code: actionCode,
                app,
                property: browser.isMirinae ? 'app' : 'web',
                os: os.name,
                ...props,
            },
        });
        console.log('GA4 user action', code, app, props);
        // todo: implement this call using the new action codes, updating tut system to match:  registerUserAction(action);
    },

    appEvent: (code, props = {}, app) => {
        const eventCode = code.replace(/-/g, '_'); // firebase prohibits '-' in event names, make all reporting consistently snake-case
        if (window.GtmChannel || window.flutter_inappwebview) {
            sendEvent('logEvent', `app_${eventCode}`, { details: code, property: 'app', app, ...props });
            return;
        }
        window.dataLayer.push({ event: undefined, details: undefined });
        window.dataLayer.push({
            event: 'app_event',
            details: {
                code: eventCode,
                app,
                property: browser.isMirinae ? 'app' : 'web',
                os: os.name,
                ...props,
            },
        });
        console.log('GA4 app event', code, app, props);
    },
};

export default GTM;

// manual dataLayer reset:
// window.google_tag_manager[{{Container ID}}].dataLayer.reset();
